import React from "react";
import { JcCommonProps, JcKeyFunction, JcUtils } from "../utils/JcUtils";

export interface JcListProps<T> extends JcCommonProps {
    readonly items: Readonly<T[]>;
    readonly itemRenderer?: (item: T) => React.ReactNode;
    readonly selection?: T;
    readonly onSelection?: (item: T) => void;
    readonly keyFunction: JcKeyFunction<T>;
    readonly noBorder?: boolean;
    /** The list's accessible name. */
    readonly ariaLabel?: string;
}

export const JC_LIST_DEFAULT_ITEM_RENDERER = <T,>(item: T): React.ReactNode => item as any;

export const JcList = <T,>(props: JcListProps<T>) => {
    const commonProps = JcUtils.commonProps(props, [
        "jc-list",
        props.noBorder && "jc-list-no-border",
    ]);

    const onSelection = props.onSelection;
    const itemRenderer = props.itemRenderer ?? JC_LIST_DEFAULT_ITEM_RENDERER;

    const renderItem = (item: T): React.ReactElement => {
        const key = props.keyFunction(item);
        const content = itemRenderer(item);

        // A selectable row is a real <button> inside the <li> — focusable and
        // keyboard-activatable — rather than a repurposed standalone JcButton, so
        // it carries the row affordance without any button chrome to strip back.
        if (onSelection) {
            const selected = props.selection === item;
            return <li key={key} className="jc-list-item">
                <button type="button"
                    className={JcUtils.classNameString(["jc-list-item-button", selected && "jc-selected"])}
                    aria-current={selected ? true : undefined}
                    onClick={() => onSelection(item)}>
                    {content}
                </button>
            </li>;
        }

        return <li key={key} className="jc-list-item">{content}</li>;
    };

    return <ul {...commonProps} aria-label={props.ariaLabel}>
        {props.items.map(renderItem)}
    </ul>;
}
